import type { Distributor, Sale, SaleOrigin, SaleType } from "@/types";
import { adminRecognizedRevenue } from "@/utils/kpi";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";

const TYPE_BADGE: Record<SaleType, string> = {
  Full: "bg-primary-500/30 text-primary-100",
  "Consignación": "bg-accent-500/20 text-accent-200",
  "A Costo": "bg-secondary-500/20 text-secondary-200",
};

const advisorLabel = (sale: Sale, distributors: Distributor[]): string => {
  if (sale.advisorId === "admin") return "Juanka (Admin)";
  const d = distributors.find((x) => x.id === sale.advisorId);
  if (!d) return sale.advisorId ? "Asesor" : "Juanka (Admin)";
  return `${d.names} ${d.surnames}`.trim() || "Asesor";
};

interface SalesTableProps {
  sales: Sale[];
  distributors: Distributor[];
}

const SalesTable = ({ sales, distributors }: SalesTableProps) => {
  if (sales.length === 0) {
    return (
      <div className="glass-panel rounded-lg p-10 text-center">
        <span className="mx-auto w-14 h-14 rounded-lg bg-accent-500/15 text-accent-200 flex items-center justify-center mb-4">
          <i className="ri-shopping-bag-3-line text-2xl" />
        </span>
        <h3 className="editable-title text-base font-bold">Sin ventas para mostrar</h3>
        <p className="text-xs text-foreground-600 mt-1">
          Registra tu primera venta del período o ajusta los filtros de búsqueda.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left">
          <thead>
            <tr className="border-b border-accent-500/20 text-[11px] uppercase tracking-wide text-foreground-500">
              <th className="px-4 py-3 font-semibold">Fecha</th>
              <th className="px-4 py-3 font-semibold">Cliente</th>
              <th className="px-4 py-3 font-semibold">Producto</th>
              <th className="px-3 py-3 font-semibold text-right">Cant.</th>
              <th className="px-3 py-3 font-semibold text-right">PV</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th>
              <th className="px-3 py-3 font-semibold">Tipo</th>
              <th className="px-3 py-3 font-semibold">Origen</th>
              <th className="px-3 py-3 font-semibold">Asesor</th>
              <th className="px-3 py-3 font-semibold text-right">Margen</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {sales.map((s) => (
              <tr
                key={s.id}
                className="border-b border-background-300/25 hover:bg-background-200/25 transition-colors"
              >
                <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{formatDate(s.date)}</td>
                <td className="px-4 py-3 font-medium text-foreground-900 max-w-[160px] truncate">
                  {s.clientName}
                </td>
                <td className="px-4 py-3 text-foreground-800 max-w-[220px] truncate">{s.productName}</td>
                <td className="px-3 py-3 text-right text-foreground-800 whitespace-nowrap">
                  {formatNumber(s.qty)}
                </td>
                <td className="px-3 py-3 text-right text-accent-200 whitespace-nowrap">
                  {formatNumber(s.pv)}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">
                  {formatCOP(adminRecognizedRevenue(s))}
                  {s.advisorId !== "admin" && (
                    <span className="block text-[10px] font-normal text-foreground-500">
                      desc. {s.marginPct}%
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${TYPE_BADGE[s.type]}`}>
                      {s.type}
                    </span>
                    {s.historical && (
                      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-secondary-500/20 text-secondary-100 whitespace-nowrap">
                        Histórico
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-foreground-700 whitespace-nowrap">{s.origin}</td>
                <td className="px-3 py-3 text-foreground-700 max-w-[150px] truncate">
                  {advisorLabel(s, distributors)}
                </td>
                <td className="px-3 py-3 text-right text-foreground-700 whitespace-nowrap">
                  {s.marginPct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-accent-500/15 text-[11px] text-foreground-500">
        <span>{formatNumber(sales.length)} ventas visibles</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-information-line" /> En ventas de asesor se muestra el valor con su descuento aplicado; Origen:{" "}
          <span className="text-accent-200">{(["Facebook", "Google", "TikTok", "Casa", "Recompra"] as SaleOrigin[]).join(" · ")}</span>
        </span>
      </div>
    </div>
  );
};

export default SalesTable;